import { css, StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import { isValidEmail } from '../../../../models/UiUtils';
import { Button } from '../../../components-v2/Button';
import {
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
	InputGroupV2 as InputGroup,
} from '../../../components-v2/InputGroup';
import { brandPrimary } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { EmailIcon } from '../../svgs/icons/EmailIcon';
import { styleSheet } from './styles';

// Shared localStorage key so the compliance recipient typed in one surface (email/social/blog) is
// remembered across the others. Mirrors the constant of the same name in ./index.tsx.
const lastUsedComplianceRecipientEmail = 'lastUsedComplianceRecipientEmail';

interface ICardStylesProps {
	cardStyles?: StyleDeclarationValue[];
}

export const ScheduleBy = ({ name, cardStyles = [] }: { name: string } & ICardStylesProps) => {
	return (
		<div className={css(styleSheet.card, ...cardStyles)}>
			<div className={css(styleSheet.cardTitle)}>Scheduled By</div>
			<div className={css(styleSheet.cardActions)}>
				<span className={css(styleSheet.approvalStatusTitle)}>{name}</span>
			</div>
		</div>
	);
};

export const ScheduleDate = ({
	scheduleDate,
	title,
	showChangeButton,
	onClick,
	disabled,
	cardStyles = [],
}: {
	scheduleDate: string;
	title: string;
	showChangeButton: boolean;
	disabled: boolean;
	onClick: () => void;
} & ICardStylesProps) => {
	return (
		<div className={css(styleSheet.card, ...cardStyles)}>
			<div className={css(styleSheet.cardTitle)}>{title}</div>
			<div className={css(styleSheet.scheduleDateActions)}>
				<time dateTime={`${moment(new Date(scheduleDate)).format('dddd, MMMM Do, YYYY, h:mm A')}`}>
					{moment(new Date(scheduleDate)).format('dddd, MMMM Do, YYYY, h:mm A')}
				</time>
				{showChangeButton ? (
					<button className={css(bs.brandLink)} disabled={disabled} onClick={onClick}>
						<span>Change</span>
					</button>
				) : null}
			</div>
		</div>
	);
};

export const ApprovalStatus = ({
	title,
	textColor,
	cardStyles = [],
}: { title: string; textColor?: string } & ICardStylesProps) => {
	return (
		<div className={css(styleSheet.card, ...cardStyles)}>
			<div className={css(styleSheet.approvalStatusTitle)} style={{ color: textColor ?? undefined }}>
				{title}
			</div>
		</div>
	);
};

/**
 * "Compliance Approval" card with the PDF re-send actions shared by email, social, and blog calendar
 * events: a "Pending Approval" status, an "Email a PDF to Compliance" action (reveals an email input so a
 * specific compliance officer can be targeted), and an "Email me/client a copy" action. This is the single
 * source of truth for the card so the three surfaces stay visually and behaviourally identical.
 *
 * Recipient resolution: an explicit address typed into the input is passed through as-is, while the
 * self/client action passes `null` so the API routes to its default recipient — the logged-in user, or the
 * impersonated client's email address when impersonating.
 *
 * `showSendActions` gates only the action buttons (the email surface hides them while editing or for
 * grouped/unscheduled campaigns) — the card title and status always render. `footer` is rendered inside the
 * card after the actions so a surface can add its own approval controls (e.g. email/social "Mark as
 * Approved").
 */
export const ComplianceSendActions = ({
	disabled,
	isImpersonating,
	selfEmail,
	onSend,
	showSendActions = true,
	footer,
	cardStyles = [],
}: {
	disabled?: boolean;
	isImpersonating: boolean;
	selfEmail: string;
	onSend: (complianceEmail: string | null) => void;
	showSendActions?: boolean;
	footer?: React.ReactNode;
} & ICardStylesProps) => {
	const [isTextInputVisible, setIsTextInputVisible] = React.useState<boolean>(false);
	const [complianceEmail, setComplianceEmail] = React.useState<string>(
		() => localStorage.getItem(lastUsedComplianceRecipientEmail) ?? ''
	);

	const checkComplianceEmail = () => {
		if (complianceEmail.length > 0 && !isValidEmail(complianceEmail)) {
			toast.error('Please enter a valid email address');
			return false;
		}
		return true;
	};

	const sendToCompliance = () => {
		if (!checkComplianceEmail()) {
			return;
		}
		localStorage.setItem(lastUsedComplianceRecipientEmail, complianceEmail);
		onSend(complianceEmail);
	};

	return (
		<section className={css(styleSheet.card, ...cardStyles)}>
			<h2 className={css(styleSheet.cardTitle)}>Compliance Approval</h2>
			<div className={css(styleSheet.cardActions)}>
				<div className={css(styleSheet.approvalStatusTitle)}>Pending Approval</div>
			</div>
			{showSendActions ? (
				<div className={css(styleSheet.complianceButtons)}>
					<Button
						variant='link'
						disabled={disabled}
						styles={[styleSheet.complianceLinkButton]}
						onClick={() => setIsTextInputVisible(visible => !visible)}
					>
						<EmailIcon className={css(styleSheet.complianceIcon)} fillColor={brandPrimary} />
						<span>Email a PDF to Compliance</span>
					</Button>
					{isTextInputVisible ? (
						<InputGroup styles={[styleSheet.complianceInputGroup]}>
							<InputGroupInput
								id='compliance-emailAddress'
								placeholder='Please enter Compliance Email'
								type='text'
								onBlur={() => {
									if (!checkComplianceEmail()) {
										return;
									}
									localStorage.setItem(lastUsedComplianceRecipientEmail, complianceEmail);
								}}
								onChange={ev => setComplianceEmail(ev.target.value)}
								value={complianceEmail}
							/>
							<InputGroupAddon align='inlineEnd'>
								<InputGroupButton
									variant='primary'
									size='sm'
									onClick={sendToCompliance}
									title='Click to Send'
									disabled={disabled}
								>
									Send
								</InputGroupButton>
							</InputGroupAddon>
						</InputGroup>
					) : null}
					<Button
						variant='link'
						disabled={disabled}
						styles={[styleSheet.complianceLinkButton]}
						// `null` recipient → API default (impersonated client's address, or the logged-in user).
						onClick={() => onSend(isImpersonating ? null : selfEmail)}
					>
						<EmailIcon className={css(styleSheet.complianceIcon)} fillColor={brandPrimary} />
						<span>{isImpersonating ? 'Email client a copy' : 'Email me a copy'}</span>
					</Button>
				</div>
			) : null}
			{footer}
		</section>
	);
};
